import React, {FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './solutions.module.scss'
import {Link, useNavigate} from "react-router-dom";
import MapView from "@arcgis/core/views/MapView";
import ArcgisMap from "@arcgis/core/Map";
import PictureMarkerSymbol from "@arcgis/core/symbols/PictureMarkerSymbol";
import { AddCityBoundaryLayer } from "../../../utils/helpers-for-2d-map";
import { LayerType } from '../../../types/layer-type';
import { LayersDataTypes } from '../../../types/layers-data-types';
import { useAppSelector } from '../../../hooks/useAppSelector';
import positionIcon from "../../../images/map-pin.svg";
import powerSupply from "../../../images/power-supply.svg";
import telecom from "../../../images/telecom.svg";
import sewerage from "../../../images/sewerage.svg";
import waterDrainage from "../../../images/water-drainage.svg";
import gasPipeline from "../../../images/gas-pipeline.svg";
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import Button from '../../ui-kit/button/button';
import Basemap from '@arcgis/core/Basemap';
import TileLayer from '@arcgis/core/layers/TileLayer';
import VectorTileLayer from '@arcgis/core/layers/VectorTileLayer';
import MapImageLayer from '@arcgis/core/layers/MapImageLayer';

const Solutions: FC = () => {
  const { t } = useTranslation();


  const pictureMarkerSymbol = new PictureMarkerSymbol({
    url: positionIcon,
    width: '36px',
    height: '36px',
    yoffset: '16px'
  });
  const navigate = useNavigate();
  const layers = useAppSelector((state) => state.map.layers);
  useEffect(() => {
    let view: MapView;

    const initializeMap = async () => {
      const map = new ArcgisMap({
        basemap: new Basemap({
          baseLayers: [
            new TileLayer({
              url: "//services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer",
              id: "hybrid-base-layer"

            }),
            new VectorTileLayer({
              url: "https://cdn.arcgis.com/sharing/rest/content/items/30d6b8271e1849cd9c3042060001f425/resources/styles/root.json"
            })
          ],
          id: "hybrid"
        })
      });
      view = new MapView({
        map: map,
        container: "solutionMapContainer",
        center: [56.1482, 21.6186],
        highlightOptions: {
          fillOpacity: 0,
          haloOpacity: 0,
        },
        zoom: 6,
        ui: {
          components: [],
        },
        popup: {
          dockOptions: {
            buttonEnabled: false,
            breakpoint: false,
          },
          visibleElements: {
            collapseButton: false,
            heading: false,
            closeButton: false,
          },
        },
      });
        layers.filter(layer => layer.url && layer.dataType === LayersDataTypes.CityBoundary && layer.type === LayerType.ArcgisFeatureServer).forEach(layer => {
          const layerProperties: any = {
            url: layer.url,
            id: layer.name,
            title: layer.label,
            visible: true,
            outFields: ["estateEn", "description"],
            popupEnabled: false
          }
          if (layer.style2D?.default) {
            layerProperties.renderer = layer.style2D?.default;
          }
          AddCityBoundaryLayer(view, layerProperties, layer, pictureMarkerSymbol);
        });
        const cityLayerIds = layers.filter(layer => layer.url && layer.dataType === LayersDataTypes.CityBoundary && layer.type === LayerType.ArcgisFeatureServer).map(layer => layer.name);
        layers.filter(layer => layer.url && layer.dataType === LayersDataTypes.OmanBorder).forEach(layer => {
          const layerProperties: any = {
            url: layer.url,
            id: layer.name,
            title: layer.label,
            visible: true,
            popupEnabled: false,
          }
          if (layer.type === LayerType.ArcgisMapServer) {
            const mapImageLayer = new MapImageLayer(layerProperties);
            if (layer.style2D?.subLayers) {
              mapImageLayer.load().then(() => {
                mapImageLayer.allSublayers.forEach(subLayer => {
                  subLayer.visible = layer.style2D!.subLayers!.includes(subLayer.id);
                })
              })
            }
            view.map.add(mapImageLayer);
          } 
          else if (layer.type === LayerType.ArcgisFeatureServer) {
            if (layer.style2D?.default) {
              layerProperties.renderer = layer.style2D?.default;
            }
            const featureLayer = new FeatureLayer(layerProperties);
            view.map.add(featureLayer);
          }
        });

      const handleClick = async (event: any) => {
        const hitResponse = await view.hitTest(event);
        const results = hitResponse.results.filter(res => cityLayerIds.includes(res.layer.id))
        if (results.length) {
          const firstResult = results[0];

          if ('graphic' in firstResult) {
            const graphic = firstResult.graphic;

            if (graphic.geometry.type === "polygon") {
              const attributes = graphic.attributes;
              const estateEn = attributes.estateEn;
              const description = attributes.description;

              const params = new URLSearchParams();

              if (estateEn) {
                params.append('eid', estateEn);
              }

              if (description) {
                params.append('cities', description);
              }

              if ([...params].length > 0) {
                navigate(`/cities?${params.toString()}`);
              }
            }
          }
        }
      };

      view.on("click", handleClick);

    };

    const task = initializeMap();

    return () => {
      task.finally(() => {
        view.map.allLayers.destroy();
        view.destroy()
      });
    };
  }, [layers]);

  return (
    <div id="solutions" className={styles.ourSolutions}>
        <div className={styles.header}>
          <h2>{t('solutions.header')}</h2>
          <h3>{t('solutions.subheader')}</h3>
        </div>
      <div className={styles.content}>
        <div className={styles.details}>
          <p className={styles.detailsHeader}>{t('solutions.benefitsHeader')}</p>
          <ul className={styles.list}>
            <li className={styles.listItem}>
              {t('solutions.benefit1')}
            </li>
            <li className={styles.listItem}>
              {t('solutions.benefit2')}
            </li>
            <li className={styles.listItem}>
              {t('solutions.benefit3')}
            </li>
            <li className={styles.listItem}>
              <div>{t('solutions.infrastructureHeader')}:
                <ul className={styles.infrastructure}>
                  <li>
                    <img className={styles.infrastructureImg} src={powerSupply} alt="power supply" /> {t('solutions.powerSupply')}
                  </li>
                  <li>
                    <img src={telecom} alt="telecom"/> {t('solutions.highSpeedTelecom')}
                  </li>
                  <li>
                    <img src={sewerage} alt="sewerage"/> {t('solutions.sewerage')}
                  </li>
                  <li>
                    <img src={waterDrainage} alt="water-drainage"/> {t('solutions.waterDrainage')}
                  </li>
                  <li>
                    <img src={gasPipeline} alt="gas"/> {t('solutions.gasPipelines')}
                  </li>
                </ul>
              </div>

            </li>
          </ul>
          <div className={styles.linkWrapper}>
            <Link to='search' style={{textDecorationLine:"none"}} className={styles.link}>
              <Button className={styles.btn}>{t('solutions.findLocationButton')}</Button>
            </Link>
          </div>
        </div>
        <div className={styles.map} id="solutionMapContainer" />
      </div>
    </div>
  );
};

export default Solutions;
